import { zodResolver } from '@hookform/resolvers/zod';
import {
  useIntegrationMqttGetAllBrokers,
  useIntegrationMqttPreviewExtraction,
  type MqttBroker,
  type MqttBufferedMessage,
} from '@smarthome/api';
import { AlertCircle, Check, ChevronDown, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { TextField, cn } from '@/components/ui';

// Schema for sensor form
const sensorFormSchema = z.object({
  id: z.string().min(1, 'ID is required').max(255),
  brokerId: z.string().min(1, 'Broker is required'),
  name: z.string().min(1, 'Name is required').max(255),
  topic: z.string().min(1, 'Topic is required').max(500),
  extract: z.string().max(1000).optional(),
  transform: z.string().max(1000).optional(),
});

export type SensorFormData = z.infer<typeof sensorFormSchema>;

export interface MqttSensorFormProps {
  /** Form ID for external submit buttons */
  formId: string;
  /** Initial values for editing, or partial values for creating */
  initialValues?: Partial<SensorFormData>;
  /** Pre-selected broker (when creating from monitor page) */
  preselectedBrokerId?: string;
  /** Sample message for live preview */
  sampleMessage?: MqttBufferedMessage | null;
  /** Called when form is submitted */
  onSubmit: (data: SensorFormData) => Promise<void>;
  /** Whether the form is in loading state */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Whether this is editing an existing sensor (hides ID field) */
  isEditing?: boolean;
  /** Optional list of brokers to use instead of fetching */
  brokers?: MqttBroker[];
}

export function MqttSensorForm({
  formId,
  initialValues,
  preselectedBrokerId,
  sampleMessage,
  onSubmit,
  isLoading: _isLoading,
  error,
  isEditing,
  brokers: providedBrokers,
}: MqttSensorFormProps) {
  const { data: brokersData } = useIntegrationMqttGetAllBrokers({
    query: { enabled: !providedBrokers },
  });
  const brokers = providedBrokers ?? brokersData?.brokers ?? [];

  const previewMutation = useIntegrationMqttPreviewExtraction();
  const [previewResult, setPreviewResult] = useState<{
    value?: number | null;
    error?: string | null;
  } | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Local state for editable payload (snapshot that doesn't update with live MQTT)
  const [editedPayload, setEditedPayload] = useState<string>(sampleMessage?.payload ?? '');

  // Reset edited payload when sampleMessage changes (panel opens with new message)
  useEffect(() => {
    setEditedPayload(sampleMessage?.payload ?? '');
  }, [sampleMessage?.payload]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SensorFormData>({
    resolver: zodResolver(sensorFormSchema),
    defaultValues: {
      id: initialValues?.id ?? '',
      brokerId: initialValues?.brokerId ?? preselectedBrokerId ?? '',
      name: initialValues?.name ?? '',
      topic: initialValues?.topic ?? sampleMessage?.topic ?? '',
      extract: initialValues?.extract ?? '',
      transform: initialValues?.transform ?? '',
    },
  });

  const extract = useWatch({ control, name: 'extract' });
  const transform = useWatch({ control, name: 'transform' });

  // Debounced preview - only runs when inputs actually change
  useEffect(() => {
    if (!editedPayload) {
      setPreviewResult(null);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      previewMutation.mutate(
        {
          data: {
            payload: editedPayload,
            extract: extract || undefined,
            transform: transform || undefined,
          },
        },
        {
          onSuccess: (data) => {
            // Pipeline: payload → extract → transform
            // Show the final result (transformed if available, else extracted)
            const error = data.extractError || data.transformError;
            const value =
              data.transformedValue !== undefined && data.transformedValue !== null
                ? data.transformedValue
                : data.extractedValue;
            setPreviewResult({ value, error });
          },
          onError: () => {
            setPreviewResult(null);
          },
        }
      );
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previewMutation is stable, excluding to prevent continuous polling
  }, [editedPayload, extract, transform]);

  // Parse payload for display (used to detect if it's valid JSON)
  const parsedPayload = useMemo(() => {
    if (!editedPayload) return null;
    try {
      return JSON.parse(editedPayload);
    } catch {
      return null;
    }
  }, [editedPayload]);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Topic display (when creating from message) */}
      {sampleMessage && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-900">
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
            Topic
          </div>
          <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">
            {sampleMessage.topic}
          </div>
        </div>
      )}

      {/* ID and Name row */}
      <div className="grid grid-cols-2 gap-4">
        {!isEditing && (
          <TextField
            label="Sensor ID"
            placeholder="living-room-temperature"
            error={errors.id?.message}
            {...register('id')}
          />
        )}
        <div className={isEditing ? 'col-span-2' : ''}>
          <TextField
            label="Name"
            placeholder="Living Room Temperature"
            error={errors.name?.message}
            {...register('name')}
          />
        </div>
      </div>

      {/* Broker selection */}
      {!preselectedBrokerId && (
        <Controller
          name="brokerId"
          control={control}
          render={({ field: { value, onChange } }) => (
            <BrokerSelect
              label="Broker"
              value={value}
              onChange={onChange}
              brokers={brokers}
              error={errors.brokerId?.message}
            />
          )}
        />
      )}

      {/* Topic input (when not from message) */}
      {!sampleMessage && (
        <TextField
          label="Topic"
          placeholder="home/livingroom/temperature"
          error={errors.topic?.message}
          {...register('topic')}
        />
      )}

      {/* Extract and Transform row */}
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label="Extract (JSONata)"
          placeholder="$.temperature"
          error={errors.extract?.message}
          {...register('extract')}
        />
        <TextField
          label="Transform (govaluate)"
          placeholder="value / 10"
          error={errors.transform?.message}
          {...register('transform')}
        />
      </div>

      {/* Live Preview Section */}
      {editedPayload && (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-900">
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
            Live Preview
          </div>

          {/* Sample Payload (Editable) */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-zinc-500">
                Sample Payload {parsedPayload && <span className="text-blue-500">(JSON)</span>}
              </span>
            </div>
            <textarea
              value={editedPayload}
              onChange={(e) => setEditedPayload(e.target.value)}
              rows={4}
              className="w-full resize-y rounded border border-gray-200 bg-gray-100 p-2 font-mono text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.08] dark:bg-zinc-800 dark:text-white"
            />
          </div>

          {/* Preview Result */}
          <div>
            <div className="text-xs text-gray-500 dark:text-zinc-500">Result</div>
            <div className="mt-1 flex items-center gap-2">
              {previewMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              ) : previewResult?.error ? (
                <span className="text-sm text-red-500">{previewResult.error}</span>
              ) : previewResult?.value !== undefined && previewResult.value !== null ? (
                <span className="font-mono text-sm text-emerald-600 dark:text-emerald-400">
                  {previewResult.value}
                </span>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          {error}
        </div>
      )}
    </form>
  );
}

// Broker dropdown component
interface BrokerSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  brokers: MqttBroker[];
  error?: string;
}

function BrokerSelect({ label, value, onChange, brokers, error }: BrokerSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedBroker = brokers.find((b) => b.id === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all',
          'bg-white dark:bg-[#111113]',
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
          isOpen && !error && 'border-blue-500 ring-1 ring-blue-500'
        )}
      >
        <span
          className={
            selectedBroker ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-zinc-500'
          }
        >
          {selectedBroker?.name ?? 'Select broker...'}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-[#1c1c1f]">
          <div className="max-h-56 overflow-y-auto">
            {brokers.map((broker) => (
              <button
                key={broker.id}
                type="button"
                onClick={() => {
                  onChange(broker.id ?? '');
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
                  value === broker.id && 'bg-blue-50 dark:bg-blue-500/10'
                )}
              >
                <div>
                  <div
                    className={cn(
                      'text-gray-900 dark:text-white',
                      value === broker.id && 'text-blue-600 dark:text-blue-400'
                    )}
                  >
                    {broker.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-zinc-500">
                    {broker.host}:{broker.port}
                  </div>
                </div>
                {value === broker.id && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}
